import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "../context/StoreContext";
import { StockAlert } from "./StockAlert";

const cardVariant = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 } }),
};

const ProductCard = ({ p, i, lang, t, soldOut, onAdd, onOpenCart }) => (
  <motion.article
    custom={i}
    variants={cardVariant}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-60px" }}
    className="group flex flex-col"
    data-testid={`product-card-${p.id}`}
  >
    <div className="relative overflow-hidden rounded-2xl aspect-square" style={{ backgroundColor: p.bgTint }}>
      <Link to={`/produit/${p.id}`} data-testid={`product-link-${p.id}`}>
        <img
          src={p.image}
          alt={p.name[lang]}
          loading={i > 2 ? "lazy" : "eager"}
          decoding="async"
          className="w-full h-full object-contain object-center p-2 sm:p-3 transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04]"
        />
      </Link>
      <div className="absolute top-4 left-4 rounded-full backdrop-blur-md bg-white/75 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.15em]" style={{ color: p.color }}>
        {p.tag[lang]}
      </div>
      {soldOut && (
        <div className="absolute inset-0 bg-white/40 backdrop-blur-[2px] flex items-center justify-center pointer-events-none">
          <span data-testid={`sold-out-badge-${p.id}`} className="rounded-full bg-stone-900 text-white px-5 py-2 text-[11px] font-mono uppercase tracking-[0.2em]">
            {t.products.soldOut}
          </span>
        </div>
      )}
      {p.effective_status === "preorder" && (
        <span data-testid={`preorder-badge-${p.id}`} className="absolute top-4 left-4 rounded-full bg-[#4A624E] text-white px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.2em]">{t.products.preorder}</span>
      )}
      {!soldOut && (
      <button
        data-testid={`add-to-cart-${p.id}`}
        onClick={() => onAdd(p)}
        aria-label={t.products.add}
        className="absolute bottom-4 right-4 w-11 h-11 rounded-full bg-stone-900 text-white flex items-center justify-center opacity-100 lg:opacity-0 lg:translate-y-2 lg:group-hover:opacity-100 lg:group-hover:translate-y-0 transition-all duration-300 hover:!bg-[#C85A32]"
      >
        <Plus size={18} />
      </button>
      )}
    </div>

    <div className="pt-5 flex flex-col flex-1">
      <div className="flex items-baseline justify-between gap-2">
        <Link to={`/produit/${p.id}`} data-testid={`product-title-link-${p.id}`}>
          <h3 className="font-serif text-xl text-stone-900 hover:text-[#C85A32] transition-colors duration-300">{p.name[lang]}</h3>
        </Link>
        <span className="font-mono text-sm text-stone-900" data-testid={`price-${p.id}`}>{p.price} CHF</span>
      </div>
      <p className="mt-1 text-[11px] font-mono uppercase tracking-[0.2em] text-stone-500">{p.subtitle[lang]} · {p.weight}</p>
      <p className="mt-3 text-sm text-stone-600 leading-relaxed flex-1">{p.desc[lang]}</p>
      <p className="mt-3 inline-flex self-start rounded-full border px-3 py-1 text-[10px] font-mono uppercase tracking-[0.12em]" style={{ borderColor: p.color, color: p.color }}>
        {p.badge[lang]}
      </p>
      {soldOut ? (
        <div className="mt-5" data-testid={`sold-out-actions-${p.id}`}>
          <StockAlert productId={p.id} compact />
        </div>
      ) : (
      <button
        data-testid={`add-to-cart-btn-${p.id}`}
        onClick={() => { onAdd(p); onOpenCart(); }}
        className="mt-5 w-full rounded-full border border-stone-900 py-3 text-[11px] uppercase tracking-[0.2em] text-stone-900 hover:bg-stone-900 hover:text-[#FAF7F2] transition-colors duration-300"
      >
        {t.products.add}
      </button>
      )}
    </div>
  </motion.article>
);

export const Products = () => {
  const { t, lang, addToCart, setCartOpen, stock, products } = useStore();

  const handleAdd = (p) => {
    addToCart(p.id);
    toast.success(`${p.name[lang]} — ${t.products.added}`);
  };

  return (
    <section id="products" className="py-24 lg:py-36 px-6 lg:px-14 xl:px-24 max-w-[1500px] mx-auto" data-testid="products-section">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16"
      >
        <div>
          <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#C85A32] mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-[#C85A32]" />
            {t.products.overline}
          </p>
          <h2 className="font-serif text-3xl lg:text-4xl tracking-tight text-stone-900 max-w-lg">{t.products.title}</h2>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {products.map((p, i) => <ProductCard key={p.id} p={p} i={i} lang={lang} t={t} soldOut={stock?.[p.id] === 0} onAdd={handleAdd} onOpenCart={() => setCartOpen(true)} />)}
      </div>
    </section>
  );
};
